/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  AuthzBoard,
  AuthzServiceNode,
  BLOCKER_LABEL,
  BoardColumn,
  COLUMNS,
  COLUMN_HELP,
  COLUMN_LABEL,
  EnvFilter,
  columnFor,
  columnForFilter,
} from './types';

const useStyles = makeStyles(theme => ({
  board: {
    display: 'grid',
    gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(180px, 1fr))`,
    gap: theme.spacing(1),
    alignItems: 'start',
  },
  column: {
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1),
    minHeight: 120,
  },
  // `notRolledOut` is the expected default state for most of the fleet during
  // the sweep, so it is deliberately quieter than `blocked`, not alarming.
  columnMuted: {
    opacity: 0.65,
  },
  columnHead: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  card: { marginBottom: theme.spacing(1) },
  envRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  pip: {
    fontSize: '0.65rem',
    height: 18,
  },
  blockers: {
    marginTop: 4,
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  selected: { outline: `2px solid ${theme.palette.primary.main}` },
  activeEnv: { fontWeight: 700, borderColor: theme.palette.primary.main },
}));

type Props = {
  board: AuthzBoard;
  teamFilter: string | null;
  envFilter: EnvFilter;
  selected: string | null;
  onSelect: (name: string | null) => void;
};

const ServiceCard = ({
  node,
  envFilter,
  selected,
  onSelect,
}: {
  node: AuthzServiceNode;
  envFilter: EnvFilter;
  selected: boolean;
  onSelect: () => void;
}) => {
  const classes = useStyles();
  // Distinct blocker codes across the envs in scope, so a card shows "chart
  // too old" once rather than three times for ci/gamedev/prod. In a
  // single-env view this is that env alone, matching the column it landed in.
  const envsInScope =
    envFilter === 'all' ? node.envs : node.envs.filter(e => e.env === envFilter);
  const codes = [
    ...new Set(envsInScope.flatMap(e => e.blockers.map(b => b.code))),
  ];
  return (
    <Card
      className={`${classes.card} ${selected ? classes.selected : ''}`}
      variant="outlined"
    >
      <CardActionArea onClick={onSelect}>
        <CardContent style={{ padding: 8 }}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {node.name}
          </Typography>
          <div className={classes.envRow}>
            {node.envs.length === 0 && (
              <Tooltip title="Could not read this service's gitops files on the last rebuild">
                <span>
                  <Chip size="small" className={classes.pip} label="unread" />
                </span>
              </Tooltip>
            )}
            {node.envs.map(e => (
              <Tooltip
                key={e.env}
                title={`${e.env}: ${COLUMN_LABEL[columnFor(e)]}${
                  e.chartRev ? ` (${e.chartRev})` : ''
                }`}
              >
                <span>
                  <Chip
                    size="small"
                    variant="outlined"
                    className={`${classes.pip} ${
                      e.env === envFilter ? classes.activeEnv : ''
                    }`}
                    label={e.env.replace('platform-', '')}
                  />
                </span>
              </Tooltip>
            ))}
          </div>
          {codes.length > 0 && (
            <div className={classes.blockers}>
              {codes.map(c => BLOCKER_LABEL[c]).join(', ')}
            </div>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

/**
 * Readiness board. Columns are rollout position, not health: with most of the
 * fleet pre-chart, `Not rolled out` and `Ready` are where most services live
 * and the board's job is to say what each service still needs.
 *
 * `envFilter` picks what classifies and ranks each row: `all` keeps the
 * worst-env aggregate (end-game reference), a specific env classifies each
 * service by that env's row alone. A service with no row for the selected env
 * is dropped from the board rather than guessed into a column.
 */
export const AuthorizationBoard = ({
  board,
  teamFilter,
  envFilter,
  selected,
  onSelect,
}: Props) => {
  const classes = useStyles();
  const services = board.services.filter(
    s => !teamFilter || s.team === teamFilter,
  );
  const columns = new Map(
    services
      .map(s => [s, columnForFilter(s, envFilter)] as const)
      .filter((e): e is [AuthzServiceNode, BoardColumn] => e[1] !== null),
  );

  return (
    <div className={classes.board}>
      {COLUMNS.map(col => {
        const inCol = [...columns.entries()]
          .filter(([, c]) => c === col)
          .map(([s]) => s);
        return (
          <Box
            key={col}
            className={`${classes.column} ${
              col === 'notRolledOut' ? classes.columnMuted : ''
            }`}
          >
            <div className={classes.columnHead}>
              <Tooltip title={COLUMN_HELP[col]}>
                <Typography variant="subtitle2">{COLUMN_LABEL[col]}</Typography>
              </Tooltip>
              <Typography variant="caption" color="textSecondary">
                {inCol.length}
              </Typography>
            </div>
            {inCol.map(s => (
              <ServiceCard
                key={s.name}
                node={s}
                envFilter={envFilter}
                selected={selected === s.name}
                onSelect={() => onSelect(selected === s.name ? null : s.name)}
              />
            ))}
          </Box>
        );
      })}
    </div>
  );
};
